import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import "./MerchPage.css";
import "./store/shop.css";
import { storeApi } from "./store/storeApi";
import { StoreProvider } from "./store/StoreContext";
import StoreShell from "./store/StoreShell";
import OrderPage from "./store/OrderPage";
import { CountdownStage } from "./store/Countdown";
import { contentOf, useFaces } from "./store/storeContent";

// /merch is the store when it's switched on in the admin (or opened with a
// preview link); a countdown teaser while its first opening is scheduled;
// otherwise the original pre-order/waitlist page below. Order pages work in
// every state, since their links are sent to customers.
const isSnapshot = typeof navigator !== "undefined" && navigator.userAgent === "ReactSnap";

const Merch = () => {
  const location = useLocation();
  const orderMatch = location.pathname.match(/^\/merch\/(order|track)\/([A-Za-z0-9_-]+)/);
  // undefined = still asking; the waitlist must never flash before the store.
  const [config, setConfig] = useState(undefined);

  const load = useCallback(
    () =>
      storeApi
        .config()
        .then(setConfig)
        .catch(() => setConfig(null)),
    []
  );
  useEffect(() => {
    // The build-time prerender gets an empty shell, never a stale catalog.
    if (!isSnapshot) load();
  }, [load]);

  // The store's display typeface, wherever a store page renders.
  const content = contentOf(config);
  useFaces(content.displayFont, content.hero.font);

  if (orderMatch) {
    return (
      <div className="merch-page store-page">
        <div className="shop" data-display={content.displayFont}>
          <main className="sh-main">
            <OrderPage token={orderMatch[2]} mode={orderMatch[1] === "track" ? "track" : "order"} />
          </main>
        </div>
      </div>
    );
  }
  if (config === undefined) return <div className="merch-page store-page shop-loading" aria-busy="true" />;
  if (config?.open) {
    return (
      <div className="merch-page store-page">
        <StoreProvider config={config}>
          <StoreShell />
        </StoreProvider>
      </div>
    );
  }
  if (config?.countdown) {
    return (
      <div className="merch-page store-page">
        <div className="shop" data-display={content.displayFont}>
          <CountdownStage countdown={config.countdown} onLaunch={load} />
        </div>
      </div>
    );
  }
  return <MerchWaitlist />;
};

const MerchWaitlist = () => {
  const [currentStep, setCurrentStep] = useState("landing"); // 'landing', 'email', 'thankyou', 'opinion1', 'opinion2', 'opinion3', 'final'
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [preferences, setPreferences] = useState({
    categories: [],
    sizes: [],
    budget: "",
  });
  const WORKER_URL = "https://the-creaks-worker.hubertdoubek12.workers.dev";
  // Animation states
  const [fadeClass, setFadeClass] = useState("fade-in");

  const transitionToStep = (newStep) => {
    setFadeClass("fade-out");
    setTimeout(() => {
      setCurrentStep(newStep);
      setFadeClass("fade-in");
    }, 300);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setIsSubmitting(true);

    try {
      const response = await fetch(`${WORKER_URL}/waitlist-merch`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        transitionToStep("thankyou");
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Subscription failed");
      }
    } catch (error) {
      console.error("Error:", error);
      alert(`Error: ${error.message}. Please check your email and try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handlePreferenceUpdate = (category, value) => {
    setPreferences((prev) => ({
      ...prev,
      [category]: value,
    }));
  };

  const handleArrayPreferenceToggle = (category, value) => {
    setPreferences((prev) => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter((item) => item !== value)
        : [...prev[category], value],
    }));
  };

  const submitPreferences = async () => {
    try {
      // The user's email is already stored in the `email` state
      await fetch(`${WORKER_URL}/merch-preferences`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: email,
          preferences: preferences,
        }),
      });
      // The worker will handle saving the preferences. We can transition regardless.
      transitionToStep("final");
    } catch (error) {
      console.error("Error submitting preferences:", error);
      // Still proceed to final step to not block the user, but we could show a message.
      alert(
        "Could not save your preferences, but you are still on the waitlist!"
      );
      transitionToStep("final");
    }
  };

  const renderLandingStep = () => (
    <div className={`merch-content ${fadeClass}`}>
      <div className="merch-hero">
        <h1 className="merch-title">MERCH STORE</h1>
        <p className="merch-subtitle">Coming Soon</p>
        <div className="merch-description">
          <p>
            We will soon be dropping merch here. We're working hard to make it
            perfect for you!
          </p>
          <p>Pre-order now to be first in line when we launch.</p>
        </div>
      </div>

      <div className="merch-cta-section">
        <button
          onClick={() => transitionToStep("email")}
          className="merch-cta-button"
        >
          Pre-Order Now
        </button>
        <p className="merch-disclaimer">
          No credit card information required for pre-orders
        </p>
      </div>
    </div>
  );

  const renderEmailStep = () => (
    <div className={`merch-content ${fadeClass}`}>
      <div className="merch-hero">
        <h1 className="merch-title">JOIN THE PRE-ORDER LIST</h1>
        <p className="merch-subtitle">Be the first to know</p>
        <div className="merch-description">
          <p>
            Enter your email to receive exclusive early access to our
            merchandise drops.
          </p>
        </div>
      </div>

      <form onSubmit={handleEmailSubmit} className="email-form">
        <div className="form-group">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email address"
            className="email-input"
            required
          />
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="merch-cta-button"
        >
          {isSubmitting ? "Subscribing..." : "Subscribe"}
        </button>
      </form>

      <button
        onClick={() => transitionToStep("landing")}
        className="back-button"
      >
        ← Back
      </button>
    </div>
  );

  const renderThankYouStep = () => (
    <div className={`merch-content ${fadeClass}`}>
      <div className="merch-hero">
        <h1 className="merch-title">THANK YOU!</h1>
        <p className="merch-subtitle">You're on the list</p>
        <div className="merch-description">
          <p>We'll notify you as soon as our merchandise becomes available.</p>
          <p>Want to help us create the perfect collection?</p>
        </div>
      </div>

      <div className="merch-cta-section">
        <button
          onClick={() => transitionToStep("opinion1")}
          className="merch-cta-button"
        >
          Share Your Preferences
        </button>
        <button
          onClick={() => transitionToStep("final")}
          className="skip-button"
        >
          Skip for now
        </button>
      </div>
    </div>
  );

  const renderOpinion1Step = () => (
    <div className={`merch-content ${fadeClass}`}>
      <div className="merch-hero">
        <h1 className="merch-title">1/3</h1>
        <p className="merch-subtitle">What interests you most?</p>
      </div>

      <div className="preferences-grid">
        {[
          {
            id: "shirt",
            label: "T-Shirt",
            desc: "A t-shirt branded by The Creaks",
          },
          {
            id: "croptop",
            label: "Crop Top",
            desc: "A cropped t-shirt branded by The Creaks",
          },
          {
            id: "hoodie",
            label: "Hoodie",
            desc: "A hoodie branded by The Creaks",
          },
          {
            id: "posters",
            label: "Poster",
            desc: "A poster with The Creaks on it",
          },
          {
            id: "music",
            label: "Physical Music",
            desc: "Vinyl, CDs, special editions",
          },
          {
            id: "book",
            label: "Story Book",
            desc: "A book about the lore within the music of The Creaks",
          },
          {
            id: "everyday-items",
            label: "Utility Items",
            desc: "Branded lighters, pens, notepads, etc.",
          },
        ].map((item) => (
          <div
            key={item.id}
            className={`preference-item ${
              preferences.categories.includes(item.id) ? "selected" : ""
            }`}
            onClick={() => handleArrayPreferenceToggle("categories", item.id)}
          >
            <h3>{item.label}</h3>
            <p>{item.desc}</p>
          </div>
        ))}
      </div>

      <div className="step-navigation">
        <button
          onClick={() => transitionToStep("thankyou")}
          className="back-button"
        >
          ← Back
        </button>
        <button
          onClick={() => transitionToStep("opinion2")}
          className="next-button"
          disabled={preferences.categories.length === 0}
        >
          Next →
        </button>
      </div>
    </div>
  );

  const renderOpinion2Step = () => (
    <div className={`merch-content ${fadeClass}`}>
      <div className="merch-hero">
        <h1 className="merch-title">2/3</h1>
        <p className="merch-subtitle">What size shirts do you usually buy?</p>
      </div>

      <div className="preferences-grid">
        {[
          {
            id: "xs",
            label: "XS",
            desc: "Extra Small (34)",
          },
          { id: "s", label: "S", desc: "Small (36)" },
          { id: "m", label: "M", desc: "Medium (38)" },
          {
            id: "l",
            label: "L",
            desc: "Large (40)",
          },
          {
            id: "xl",
            label: "XL",
            desc: "Extra Large (42)",
          },
          {
            id: "xxl",
            label: "XXL",
            desc: "Extra extra large (44+)",
          },
        ].map((item) => (
          <div
            key={item.id}
            className={`preference-item ${
              preferences.sizes.includes(item.id) ? "selected" : ""
            }`}
            onClick={() => handleArrayPreferenceToggle("sizes", item.id)}
          >
            <h3>{item.label}</h3>
            <p>{item.desc}</p>
          </div>
        ))}
      </div>

      <div className="step-navigation">
        <button
          onClick={() => transitionToStep("opinion1")}
          className="back-button"
        >
          ← Back
        </button>
        <button
          onClick={() => transitionToStep("opinion3")}
          className="next-button"
          disabled={preferences.sizes.length === 0}
        >
          Next →
        </button>
      </div>
    </div>
  );

  const renderOpinion3Step = () => (
    <div className={`merch-content ${fadeClass}`}>
      <div className="merch-hero">
        <h1 className="merch-title">3/3</h1>
        <p className="merch-subtitle">What's your budget range?</p>
      </div>

      <div className="preferences-grid">
        {[
          {
            id: "budget",
            label: "Budget-Friendly",
            desc: "Under 200 CZK",
            value: "budget",
          },
          { id: "mid", label: "Mid-Range", desc: "200-400 CZK", value: "mid" },
          {
            id: "premium",
            label: "Premium",
            desc: "400-600 CZK",
            value: "premium",
          },
          {
            id: "luxury",
            label: "Luxury",
            desc: "600-1000 CZK",
            value: "luxury",
          },
        ].map((item) => (
          <div
            key={item.id}
            className={`preference-item ${
              preferences.budget === item.value ? "selected" : ""
            }`}
            onClick={() => handlePreferenceUpdate("budget", item.value)}
          >
            <h3>{item.label}</h3>
            <p>{item.desc}</p>
          </div>
        ))}
      </div>

      <div className="step-navigation">
        <button
          onClick={() => transitionToStep("opinion2")}
          className="back-button"
        >
          ← Back
        </button>
        <button
          onClick={submitPreferences}
          className="next-button"
          disabled={!preferences.budget}
        >
          Complete
        </button>
      </div>
    </div>
  );

  const renderFinalStep = () => (
    <div className={`merch-content ${fadeClass}`}>
      <div className="merch-hero">
        <h1 className="merch-title">ALL SET!</h1>
        <p className="merch-subtitle">Thanks for your input</p>
        <div className="merch-description">
          <p>
            Your preferences have been saved. We'll use this information to
            create merchandise that you'll love.
          </p>
          <p>Keep an eye on your inbox for exclusive updates!</p>
        </div>
      </div>

      <div className="merch-cta-section">
        <button
          onClick={() => (window.location.href = "/")}
          className="merch-cta-button"
        >
          Back to Home
        </button>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case "landing":
        return renderLandingStep();
      case "email":
        return renderEmailStep();
      case "thankyou":
        return renderThankYouStep();
      case "opinion1":
        return renderOpinion1Step();
      case "opinion2":
        return renderOpinion2Step();
      case "opinion3":
        return renderOpinion3Step();
      case "final":
        return renderFinalStep();
      default:
        return renderLandingStep();
    }
  };

  return (
    <div className="merch-page">
      <div className="merch-container">{renderCurrentStep()}</div>
    </div>
  );
};

export default Merch;
